import { Component, forwardRef, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { AuthentificationService } from '@app/authentification/authentification.service';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';
import { PrFormConstructor } from '@app/components/pr-form/pr-form.contructor';
import {
  BudgetAggregation,
  BudgetDao,
  CreateBudget,
} from '@app/dao/budget/budget.dao';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Subject } from 'rxjs';
import { EaBudgetFormBuilder } from './ea-budget-form.builder';

@UntilDestroy()
@Component({
  selector: 'ea-budget-form',
  templateUrl: './ea-budget-form.component.html',
  styleUrls: ['./ea-budget-form.component.scss'],
  standalone: false,
  providers: [
    {
      provide: PrFormAbstract,
      useExisting: forwardRef(() => EaBudgetFormComponent),
    },
  ],
})
export class EaBudgetFormComponent
  extends EaBudgetFormBuilder
  implements OnInit
{
  budgetTypes = [
    { id: 'EXPENSE', label: 'Dépense' },
    { id: 'EARNING', label: 'Revenu' },
  ];

  frequencies = [
    { id: 'MENS', label: 'Mensuel', suffix: '€/mois' },
    { id: 'ANNU', label: 'Annuel', suffix: '€/an' },
  ];

  doneSubject: Subject<any> = new Subject();

  allTypes: any[] = [];
  collapsedStates: { [key: number]: boolean } = {};
  isAutomaticGroupOpen = false;
  accountId: number | null = null;
  private _manualBudgets: number[] = [];
  private _automaticBudgets: number[] = [];

  constructor(
    protected formConstructor: PrFormConstructor,
    private budgetDao: BudgetDao,
    private authService: AuthentificationService,
  ) {
    super(formConstructor);
  }

  get title() {
    const budgetType = this.entity?.[0]?.budgetType || 'EXPENSE';
    return budgetType === 'EARNING'
      ? 'Gestion des revenus'
      : 'Gestion des dépenses';
  }

  ngOnInit() {
    this.loadAllTypes();
    this.loadAccountId();
  }

  initForm(entity: any, options?: any) {
    super.initForm(entity, options);

    this.entity = this.getBudgetTypeFromOptions(entity, options);

    this.budgetsArray.controls.forEach((_, index) => {
      this.collapsedStates[index] = index !== 0;
    });

    this.refreshBudgetIndices();
    this.fixBudgetsAccountId();
  }

  private getBudgetTypeFromOptions(entity: any, options?: any): any {
    if (options?.budgetType) {
      return [{ budgetType: options.budgetType }];
    }
    if (!entity || entity.length === 0) {
      return [{ budgetType: 'EXPENSE' }];
    }
    return entity;
  }

  loadAccountId() {
    this.authService.authentication.pipe(untilDestroyed(this)).subscribe(user => {
      this.accountId = user?.accountId;
      this.fixBudgetsAccountId();
    });
  }

  private fixBudgetsAccountId() {
    if (this.accountId && this.budgetsArray) {
      this.budgetsArray.controls.forEach(control => {
        const formGroup = control as FormGroup;
        const accountIdControl = formGroup.get('accountId');
        if (!accountIdControl?.value) {
          accountIdControl?.setValue(this.accountId);
        }
      });
    }
  }

  loadAllTypes() {
    this.budgetDao.getEarningTypes().pipe(untilDestroyed(this)).subscribe({
      next: types => {
        const earningTypes = types.map(t => ({
          ...t,
          budgetType: 'EARNING',
        }));
        this.allTypes = [
          ...this.allTypes.filter(t => t.budgetType !== 'EARNING'),
          ...earningTypes,
        ];
      },
    });

    this.budgetDao.getExpenseTypes().pipe(untilDestroyed(this)).subscribe({
      next: types => {
        const expenseTypes = types.map(t => ({
          ...t,
          budgetType: 'EXPENSE',
        }));
        this.allTypes = [
          ...this.allTypes.filter(t => t.budgetType !== 'EXPENSE'),
          ...expenseTypes,
        ];
      },
    });
  }

  get manualBudgets(): number[] {
    return this._manualBudgets;
  }

  get automaticBudgets(): number[] {
    return this._automaticBudgets;
  }

  private refreshBudgetIndices() {
    this._manualBudgets = [];
    this._automaticBudgets = [];
    this.budgetsArray.controls.forEach((control, index) => {
      if ((control as FormGroup).get('automatic')?.value) {
        this._automaticBudgets.push(index);
      } else {
        this._manualBudgets.push(index);
      }
    });
  }

  getCategories(budgetType: string) {
    const filtered = this.allTypes.filter(t => t.budgetType === budgetType);

    const uniqueCategories = [
      ...new Map(
        filtered.map(t => [
          t.category.id,
          { id: t.category.id, label: t.category.value },
        ]),
      ).values(),
    ];

    return uniqueCategories;
  }

  getTypes(budgetType: string, categoryId: string) {
    const filtered = this.allTypes
      .filter(t => t.budgetType === budgetType && t.category.id === categoryId)
      .filter(t => !t.automatic);

    const uniqueCategories = [
      ...new Map(
        filtered.map(t => [t.id, { id: t.id, label: t.label }]),
      ).values(),
    ];

    return uniqueCategories;
  }

  getCategoriesLabel(budget: BudgetAggregation): string {
    const type = this.allTypes.find(t => t.id == budget.type);
    const category = type?.category;
    return category?.value;
  }

  getTypesLabel(budget: BudgetAggregation): string {
    const type = this.allTypes.find(t => t.id == budget.type);
    return type?.label;
  }

  addBudget() {
    const budgetType = this.entity?.[0]?.budgetType || 'EXPENSE';
    const newBudget = this.createBudgetFormGroup({
      accountId: this.accountId,
      budgetType: budgetType,
      frequency: 'MENS',
      automatic: false,
    });
    this.budgetsArray.push(newBudget);
    const newIndex = this.budgetsArray.length - 1;
    this.collapsedStates[newIndex] = false;
    this.refreshBudgetIndices();
  }

  removeBudget(index: number) {
    const budgetGroup = this.budgetsArray.at(index) as FormGroup;
    const budgetId = budgetGroup.get('id')?.value;

    if (budgetId) {
      this.budgetDao
        .deleteBudget({
          id: budgetId,
          accountId: this.accountId!,
        } as any)
        .subscribe({
          next: () => {
            this.budgetsArray.removeAt(index);
            delete this.collapsedStates[index];
            this.refreshBudgetIndices();
            this.notificationService.success('Budget supprimé');
          },
          error: err => {
            console.error(err);
            this.notificationService.error('Erreur lors de la suppression');
          },
        });
    } else {
      this.budgetsArray.removeAt(index);
      delete this.collapsedStates[index];
      this.refreshBudgetIndices();
    }
  }

  toggleCollapse(index: number) {
    this.collapsedStates[index] = !this.collapsedStates[index];
  }

  isCollapsed(index: number): boolean {
    return this.collapsedStates[index] !== false;
  }

  getBudgetTitle(index: number): string {
    const budgetGroup = this.budgetsArray.at(index) as FormGroup;
    const label = budgetGroup.get('label')?.value;
    const typeId = budgetGroup.get('type')?.value;
    const budgetId = budgetGroup.get('id')?.value;
    const isAutomatic = budgetGroup.get('automatic')?.value;

    const type = this.allTypes.find(t => t.id === typeId);

    if (!budgetId && !isAutomatic) {
      return `Budget ${index + 1}`;
    }

    return `Budget ${index + 1} ${label ? `- ${label}` : type?.label ? `- ${type.label}` : ''}`;
  }

  onSubmit() {
    this.prFormService.loading();
    const budgets = this.extract().filter((b: CreateBudget) => !b.automatic);

    this.budgetDao.saveBudget(budgets).subscribe({
      next: () => {
        this.prFormService.loaded();
        this.notificationService.success('La sauvegarde a réussi');
        this.postDoneMessage();
        this.prFormService.close();
      },
      error: (err: any) => {
        console.error(err);
        this.prFormService.loaded();
        this.notificationService.error('La sauvegarde a échoué');
        this.prFormService.close();
      },
    });
  }
}
